import { z } from 'zod';

import {
  parseMarketplaceOrderHandoffDefaults,
  type MarketplaceOrderHandoffDefaults,
} from './marketplace-order-handoff-defaults.js';
import { looksLikeHandoffUrl } from './marketplace-order-handoff-source.js';
import { isVendorInaccessibleUrl } from './marketplace-order-handoff-snapshot.js';
import { TargetRatiosFieldSchema } from './target-ratios.js';

export const VENDOR_HANDOFF_TEMPLATE_VERSION = 'vendor-handoff-v3';
export const VENDOR_HANDOFF_FROM_FALLBACK = 'orders@adge.ai';
export const VENDOR_HANDOFF_SHORT_REF_LENGTH = 6;
/** Longest id suffix ops order search treats as `endsWith` instead of an exact id. */
export const OPS_ORDER_ID_SUFFIX_MAX_LENGTH = 8;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PLACEHOLDER_EXACT = new Set([
  'test',
  'sd',
  'asdf',
  'asdfg',
  'foo',
  'bar',
  'xxx',
  'lorem',
  'placeholder',
  'tba',
  'tbd',
  'n/a',
  'na',
]);
const PLACEHOLDER_PREFIX = /^(?:dfsf|ghjg|asdf|xxxx|test)/i;

/** One opening hook of a script; each hook is shot as its own video. Position 1 is the primary. */
export const VendorHandoffHookSchema = z.object({
  position: z.number().int().min(1),
  voiceover: z.string(),
});
export type VendorHandoffHook = z.infer<typeof VendorHandoffHookSchema>;

export const VendorHandoffScriptSchema = z.object({
  id: z.string(),
  title: z.string().min(1),
  hook: z.string().nullable(),
  previewUrl: z.string().nullable(),
  downloadUrl: z.string().nullable().optional(),
  dueAt: z.string().nullable(),
  isCustom: z.boolean(),
  assetKind: z.enum(['SCRIPT', 'BRIEF']).nullable().optional(),
  storyboardFilenames: z.array(z.string()).default([]),
  /** Primary first. Empty for briefs, legacy and custom scripts, which count as one video. */
  hooks: z.array(VendorHandoffHookSchema).default([]),
  /** Optional link the client added on send for the partner to match. */
  referenceVideoUrl: z.string().nullable().optional(),
});
export type VendorHandoffScript = z.infer<typeof VendorHandoffScriptSchema>;

export const VendorHandoffEmailInputSchema = z.object({
  brandName: z.string().min(1),
  contactName: z.string().min(1),
  from: z.string().nullable(),
  replyTo: z.string().nullable(),
  dueAt: z.string().nullable(),
  sentAt: z.string().nullable().optional(),
  timeZone: z.string().min(1).default('UTC'),
  orderId: z.string().nullable(),
  orderRef: z.string().nullable(),
  vendorName: z.string().nullable(),
  logoUrl: z.string().nullable(),
  dashboardUrl: z.string().nullable(),
  selectedPackage: z
    .object({
      name: z.string(),
      keyTerms: z.string().nullable(),
    })
    .nullable()
    .optional(),
  orderInstructions: z.string(),
  productAccuracy: z.string(),
  rules: z.object({
    brandGuidelines: z.string(),
    techSpecs: z.string(),
    policyRestrictions: z.string(),
    dosAndDonts: z.string(),
  }),
  brandKit: z.object({
    fontsLicenceProof: z.string(),
    coloursHex: z.string(),
    logos: z.string(),
  }),
  /**
   * Thumbnail guidelines from the order snapshot.
   * null omits the section (orders that never recorded the field).
   * A blank string was snapshotted and the renderer uses the Adge default.
   */
  thumbnailGuidelines: z.string().nullable().default(null),
  scripts: z.array(VendorHandoffScriptSchema),
  subjectOverride: z.string().nullable().optional(),
  /** Cuts the partner should deliver. Empty on orders sent before the field existed. */
  targetRatios: TargetRatiosFieldSchema,
});
export type VendorHandoffEmailInput = z.infer<typeof VendorHandoffEmailInputSchema>;

export const VendorHandoffCheckSeveritySchema = z.enum(['blocker', 'warning']);
export type VendorHandoffCheckSeverity = z.infer<typeof VendorHandoffCheckSeveritySchema>;

export const VendorHandoffCheckSchema = z.object({
  code: z.string(),
  severity: VendorHandoffCheckSeveritySchema,
  message: z.string(),
  field: z.string().optional(),
});
export type VendorHandoffCheck = z.infer<typeof VendorHandoffCheckSchema>;

export const VENDOR_HANDOFF_CHECK_FIELD_LABELS: Record<string, string> = {
  orderInstructions: 'Order-specific instructions',
  productAccuracy: 'Product accuracy',
  brandGuidelines: 'Brand guidelines',
  techSpecs: 'Tech specs',
  policyRestrictions: 'Policy & restrictions',
  dosAndDonts: "Do's & Don'ts",
  fontsLicenceProof: 'Fonts + licence proof',
  coloursHex: 'Colours / hex',
  logos: 'Logos',
  thumbnailGuidelines: 'Thumbnail guidelines',
};

function handoffFieldLabel(key: string): string {
  return VENDOR_HANDOFF_CHECK_FIELD_LABELS[key] ?? key;
}

export function isVendorHandoffEmailAddress(value: string): boolean {
  return EMAIL_PATTERN.test(value.trim());
}

export function resolveMarketplaceOrderFromEmail(
  configuredFromEmail?: string | null,
): string {
  const configured = configuredFromEmail?.trim();
  return configured && isVendorHandoffEmailAddress(configured)
    ? configured
    : VENDOR_HANDOFF_FROM_FALLBACK;
}

/**
 * The orders mailbox does not keep a copy of what it sends, so mail from it
 * BCCs itself to keep the monitored inbox a full archive. Other configured
 * senders (e.g. staging) get no automatic BCC.
 */
export function resolveMonitoredHandoffBcc(input: {
  from: string;
  to: string | readonly string[];
  cc?: readonly string[];
}): string[] {
  if (!isSameEmailAddress(input.from, VENDOR_HANDOFF_FROM_FALLBACK)) return [];
  const visible = [...(typeof input.to === 'string' ? [input.to] : input.to), ...(input.cc ?? [])];
  const isAlreadyRecipient = visible.some((address) =>
    isSameEmailAddress(address, VENDOR_HANDOFF_FROM_FALLBACK),
  );
  return isAlreadyRecipient ? [] : [VENDOR_HANDOFF_FROM_FALLBACK];
}

function isSameEmailAddress(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

export function shortOrderRef(orderId: string | null | undefined): string | null {
  const trimmed = orderId?.trim() ?? '';
  if (!trimmed) return null;
  return trimmed.slice(-VENDOR_HANDOFF_SHORT_REF_LENGTH).toUpperCase();
}

export function formatScriptCountLabel(count: number): string {
  return count === 1 ? '1 script' : `${count} scripts`;
}

function formatVideoCountLabel(count: number): string {
  return count === 1 ? '1 video' : `${count} videos`;
}

/** Each hook is one video; a script without hooks is still one video. */
export function countVendorHandoffVideos(
  scripts: ReadonlyArray<{ hooks?: ReadonlyArray<unknown> }>,
): number {
  return scripts.reduce((total, script) => total + Math.max(1, script.hooks?.length ?? 0), 0);
}

/** "2 scripts · 4 videos" when any script has alternates, else today's "2 scripts". */
export function formatScriptAndVideoCountLabel(scriptCount: number, videoCount: number): string {
  const scripts = formatScriptCountLabel(scriptCount);
  return videoCount > scriptCount ? `${scripts} · ${formatVideoCountLabel(videoCount)}` : scripts;
}

const VENDOR_DUE_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

export function formatVendorDueDate(
  value: string | Date | null | undefined,
  _locale?: string,
): string | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return `${date.getUTCDate()} ${VENDOR_DUE_MONTHS[date.getUTCMonth()]}`;
}

export function buildVendorHandoffSubject(input: {
  brandName: string;
  scriptCount: number;
  videoCount?: number;
  dueAt?: string | Date | null;
  locale?: string;
  orderId?: string | null;
  orderRef?: string | null;
}): string {
  const brand = input.brandName.trim() || 'Adge';
  const parts = [
    `[${brand}] New production order`,
    formatScriptAndVideoCountLabel(input.scriptCount, input.videoCount ?? input.scriptCount),
  ];
  const due = formatVendorDueDate(input.dueAt, input.locale);
  if (due) parts.push(`due ${due}`);
  const ref = input.orderRef?.trim() || shortOrderRef(input.orderId);
  if (ref) parts.push(ref);
  return parts.join(' · ');
}

export function looksLikePlaceholderHandoffText(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed || looksLikeHandoffUrl(trimmed)) return false;
  if (PLACEHOLDER_EXACT.has(trimmed.toLowerCase())) return true;
  if (trimmed.length <= 3) return true;
  return !/\s/.test(trimmed) && PLACEHOLDER_PREFIX.test(trimmed);
}

export function collectHandoffFieldValues(input: {
  orderInstructions?: string;
  defaults: MarketplaceOrderHandoffDefaults;
}): Array<{ key: string; value: string }> {
  const defaults = parseMarketplaceOrderHandoffDefaults(input.defaults);
  return [
    { key: 'orderInstructions', value: input.orderInstructions ?? '' },
    { key: 'productAccuracy', value: defaults.productAccuracy },
    { key: 'brandGuidelines', value: defaults.brandGuidelines },
    { key: 'techSpecs', value: defaults.techSpecs },
    { key: 'policyRestrictions', value: defaults.policyRestrictions },
    { key: 'dosAndDonts', value: defaults.dosAndDonts },
    { key: 'fontsLicenceProof', value: defaults.fontsLicenceProof },
    { key: 'coloursHex', value: defaults.coloursHex },
    { key: 'logos', value: defaults.logos },
    { key: 'thumbnailGuidelines', value: defaults.thumbnailGuidelines },
  ];
}

export function evaluateVendorHandoffChecks(input: {
  to: readonly string[];
  cc?: readonly string[];
  bcc?: readonly string[];
  subject: string;
  dueAt?: string | Date | null;
  orderInstructions?: string;
  defaults: MarketplaceOrderHandoffDefaults;
}): { blockers: VendorHandoffCheck[]; warnings: VendorHandoffCheck[]; canSend: boolean } {
  const blockers: VendorHandoffCheck[] = [];
  const warnings: VendorHandoffCheck[] = [];
  const to = input.to.map((value) => value.trim()).filter(Boolean);
  const extras = [...(input.cc ?? []), ...(input.bcc ?? [])]
    .map((value) => value.trim())
    .filter(Boolean);

  if (to.length === 0) {
    blockers.push({
      code: 'no_recipients',
      severity: 'blocker',
      message: 'Add at least one To recipient.',
    });
  }
  for (const address of [...to, ...extras]) {
    if (!isVendorHandoffEmailAddress(address)) {
      blockers.push({
        code: 'invalid_address',
        severity: 'blocker',
        message: `${address} is not a valid email address.`,
      });
    }
  }
  if (!input.subject.trim()) {
    blockers.push({
      code: 'empty_subject',
      severity: 'blocker',
      message: 'Subject cannot be empty.',
    });
  }

  const fields = collectHandoffFieldValues({
    orderInstructions: input.orderInstructions,
    defaults: input.defaults,
  });
  for (const field of fields) {
    const label = handoffFieldLabel(field.key);
    if (looksLikePlaceholderHandoffText(field.value)) {
      warnings.push({
        code: 'placeholder_text',
        severity: 'warning',
        field: field.key,
        message: `${label} looks like placeholder text.`,
      });
    }
    if (isVendorInaccessibleUrl(field.value)) {
      warnings.push({
        code: 'internal_link',
        severity: 'warning',
        field: field.key,
        message: `${label} includes a link production partners cannot open.`,
      });
    }
  }
  if (!input.dueAt) {
    warnings.push({
      code: 'missing_due_date',
      severity: 'warning',
      message: 'No due date is set for this order.',
    });
  }

  return { blockers, warnings, canSend: blockers.length === 0 };
}

const FORBIDDEN_HTML_TAGS = /<\/?(?:script|iframe|object|embed|link|meta|base|form)[^>]*>/gi;
const EVENT_HANDLER_ATTR = /\son[a-z]+\s*=\s*(['"]).*?\1/gi;
const JAVASCRIPT_URL = /(?:href|src)\s*=\s*(['"])\s*javascript:[^'"]*\1/gi;

/** Defense in depth for stored HTML. The iframe sandbox is the primary guard. */
export function sanitizeVendorEmailHtml(html: string): string {
  return html
    .replace(FORBIDDEN_HTML_TAGS, '')
    .replace(EVENT_HANDLER_ATTR, '')
    .replace(JAVASCRIPT_URL, '');
}

export function parseVendorHandoffEmailInput(
  raw: unknown,
): VendorHandoffEmailInput | null {
  const parsed = VendorHandoffEmailInputSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}
